import { ActionTypes } from '../constants/AppConstants';
import AppDispatcher from '../dispatcher/AppDispatcher';
import FareOnlyService from '../services/FareOnlyService';
import SeasonTicketsService from '../services/SeasonTicketsService';
import SeasonSearch from '../records/SeasonSearch';
import ExtendableError from '../lib/ExtendableError';
import { InvalidSearchError } from '../errors/Search';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import PromoCodeActions from './PromoCodeActions';
import PromoCode from '../records/PromoCode';

export class InvalidSeasonSearchError extends ExtendableError {
    constructor (message, search) {
        const params = JSON.stringify(search.serialize());
        super(message, {params});
    }

    name = 'InvalidSeasonSearchError';
}

const SeasonSearchActions = {

    async loadFromParams(params, query) {
        let search = await SeasonSearch.deserialize(params, query);
        search = search.set('submitted', true);

        this.checkPromoFromQuery(search);

        return this.load(search);
    },

    load(search) {
        AppDispatcher.dispatch({
            search,
            actionType: ActionTypes.SEASON_SEARCH_LOADED
        });
        return search;
    },

    update (search) {
        AppDispatcher.dispatch({
            search,
            actionType: ActionTypes.SEASON_SEARCH_UPDATED
        });
    },

    async checkPromoFromQuery(search) {
        const promoCode = search.getPromoCode();
        const enablePromoDiscount = FeatureFlagStore.get('enablePromoDiscount');
        const enablePromoDiscountValidation = FeatureFlagStore.get('enablePromoDiscountValidation');

        if (!enablePromoDiscount || !promoCode) {
            return;
        }

        if (enablePromoDiscountValidation) {
            const validatedPromoCode = await PromoCodeActions.validatePromoCode(promoCode);
            const newSearch = search.setPromoCode(validatedPromoCode);

            this.update(newSearch);
        } else {
            const promo = new PromoCode({ promoCode });
            await PromoCodeActions.apply(promo);
            const newSearch = search.setPromoCode(promo);

            this.update(newSearch);
        }
    },

    async preFillPromoFromQuery(query) {
        const enablePromoDiscount = FeatureFlagStore.get('enablePromoDiscount');

        if (!enablePromoDiscount) {
            return;
        }

        const newSearch = new SeasonSearch({ promoCode: new PromoCode(query) });

        this.update(newSearch);
    },

    async search ({ search, user }) {
        if (!search.isValid()) {
            const errors = search.getValidationErrors().toJS().join(', ');

            AppDispatcher.dispatch({
                search,
                actionType: ActionTypes.SEASON_SEARCH_FAILED
            });

            throw new InvalidSeasonSearchError(`Failed check: ${errors}`, search);
        }

        AppDispatcher.dispatch({
            search,
            actionType: ActionTypes.SEASON_SEARCH_STARTED
        });

        const result = await SeasonTicketsService.search(search, user);
        search = search.set('submitted', true);
        AppDispatcher.dispatch({
            search,
            actionType: ActionTypes.SEASON_SEARCH_FINISHED,
            seasonTickets: result
        });

        return {
            search,
            result
        };
    },

    async searchFares ({ search, user }) {
        if (!search.isValid()) {
            const errors = search.getValidationErrors().toJS().join(', ');

            AppDispatcher.dispatch({
                actionType: ActionTypes.FARE_ONLY_SEASON_SEARCH_FAILED,
                search
            });

            return Promise.reject(new InvalidSearchError(`Failed check: ${errors}`, search));
        }

        AppDispatcher.dispatch({
            actionType: ActionTypes.FARE_ONLY_SEASON_SEARCH_STARTED,
            search
        });

        const fares = await FareOnlyService.seasonSearch(search, user);

        AppDispatcher.dispatch({
            actionType: ActionTypes.FARE_ONLY_SEASON_SEARCH_FINISHED,
            search,
            fares
        });

        return {
            search,
            fares
        };
    },

    changeSeasonTicketFilters (seasonTickets, filters) {
        AppDispatcher.dispatch({
            seasonTickets,
            filters,
            actionType: ActionTypes.SEASON_RESULTS_FILTERS_CHANGE
        });
    }

};

export default SeasonSearchActions;
